import { cloneElement, isValidElement, type ReactElement, type MouseEvent } from "react";

// React equivalent of the Vue `v-aha-emit-action` directive.
//
// The presenter has a window message listener
// (`vuex/plugins/mixpanel.context.plugin.js → _handleMessageFromIframe`)
// that accepts:
//
//   { type: "userAction", eventName, name, properties }
//
// from iframes and routes it through the same auto-tracking pipeline
// the directive uses. The pipeline produces Mixpanel events named
//   `${action}_${snake_case(name)}_${snake_case(other_info)}`
// — e.g. `click_invite_member_button` — so the analytics dashboard sees
// identical event names whether the surface lives in the presenter or
// in this embed.
//
// Usage — wrap a single clickable child:
//
//   <TrackOnClick name="invite-member-button">
//     <Button onClick={openInviteModal}>Invite</Button>
//   </TrackOnClick>
//
// Pass extra context via `properties` (the directive's binding value,
// picked up by the host as `customProps` on the Mixpanel event):
//
//   <TrackOnClick name="upgrade-team-button" properties={{ plan: "free" }}>
//     <Button onClick={openUpgrade}>Upgrade team plan</Button>
//   </TrackOnClick>
//
// For non-click events or imperative tracking inside handlers, use
// `useTrack()` and call `track(...)` directly.

type ClickableProps = { onClick?: (e: MouseEvent) => void };

export interface TrackOnClickProps {
  /** Identifier — becomes the `<name>` part in `click_<name>`. */
  name: string;
  /** Forwarded as `customProps` on the Mixpanel event — matches the
   *  v-aha-emit-action directive's binding value. */
  properties?: Record<string, unknown>;
  children: ReactElement<ClickableProps>;
}

// Post a userAction message to the host's iframe listener, which routes it
// through the same auto-tracking pipeline the v-aha-emit-action directive uses.
// `eventName` is the raw DOM event name — the host maps it through its
// eventActions table (click→click, mouseenter→hover, dblclick→double_click,
// focus, blur, change, submit) and produces Mixpanel `${action}_${snake_case(
// name)}`. An UNMAPPED eventName falls back to `undefined_action_…`, so pass the
// DOM event, not the action label (e.g. "mouseenter", never "hover"). Exported
// so non-click surfaces emit the SAME unified events as the presenter.
//
// Standalone dev (no parent) → no-op. targetOrigin is `*` because the host's
// listener doesn't validate origin; tightening would require knowing the
// presenter's origin at compile time per env.
export function postUserAction(
  eventName: string,
  name: string,
  properties: Record<string, unknown> = {},
) {
  if (typeof window === "undefined" || window.parent === window) return;
  try {
    window.parent.postMessage(
      { type: "userAction", eventName, name, properties },
      "*",
    );
  } catch {
    // postMessage can throw in sandboxed contexts — never leak into the caller.
  }
}

export function TrackOnClick({ name, properties, children }: TrackOnClickProps) {
  if (!isValidElement<ClickableProps>(children)) return children;

  const childOnClick = children.props.onClick;
  return cloneElement<ClickableProps>(children, {
    onClick: (e: MouseEvent) => {
      postUserAction("click", name, properties ?? {});
      childOnClick?.(e);
    },
  });
}
